import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag, message, Dropdown, Modal, Form, Switch, Checkbox } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined, DeleteOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/design-system/Card';
import api from '../services/api';

interface Carrier {
  id: string;
  scac: string;
  name: string;
  shortName: string | null;
  sctc: boolean;
  portcastEnabled: boolean;
  active: boolean;
  bookings_count: number;
}

interface CarriersResponse {
  carriers: Carrier[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

interface PortcastCarrier {
  scac: string;
  name: string;
  exists: boolean;
}

const CarriersList: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [portcastModalVisible, setPortcastModalVisible] = useState(false);
  const [selectedPortcastScacs, setSelectedPortcastScacs] = useState<string[]>([]);
  const [form] = Form.useForm();

  const { data, isLoading, error } = useQuery<CarriersResponse>({
    queryKey: ['carriers', searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '200');
      return await api.get(`/carriers?${params.toString()}`);
    },
  });

  const carriers = data?.carriers || [];

  const { data: portcastData, refetch: refetchPortcast } = useQuery<{ carriers: PortcastCarrier[] }>({
    queryKey: ['portcast-carriers'],
    queryFn: async () => await api.get('/carriers/portcast/available'),
    enabled: false,
  });

  const portcastCarriers = portcastData?.carriers || [];

  const importPortcastMutation = useMutation({
    mutationFn: async (scacs: string[]) => {
      return await api.post('/carriers/portcast/import', { scacs });
    },
    onSuccess: (data: any) => {
      message.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      setPortcastModalVisible(false);
      setSelectedPortcastScacs([]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to import carriers');
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: any) => {
      return await api.post('/carriers', values);
    },
    onSuccess: () => {
      message.success('Carrier created');
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create carrier');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: any }) => {
      return await api.patch(`/carriers/${id}`, values);
    },
    onSuccess: () => {
      message.success('Carrier updated');
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update carrier');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await api.delete(`/carriers/${id}`);
    },
    onSuccess: () => {
      message.success('Carrier deactivated');
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete carrier');
    },
  });

  const handleCreate = () => {
    setSelectedCarrier(null);
    setModalMode('create');
    form.resetFields();
    form.setFieldsValue({ sctc: false, portcastEnabled: false });
    setModalVisible(true);
  };

  const handleEdit = (carrier: Carrier) => {
    setSelectedCarrier(carrier);
    setModalMode('edit');
    form.setFieldsValue({
      scac: carrier.scac,
      name: carrier.name,
      shortName: carrier.shortName,
      sctc: carrier.sctc,
      portcastEnabled: carrier.portcastEnabled,
      active: carrier.active,
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedCarrier(null);
    form.resetFields();
  };

  const handlePortcastImport = () => {
    refetchPortcast();
    setSelectedPortcastScacs([]);
    setPortcastModalVisible(true);
  };

  const handlePortcastSubmit = () => {
    if (selectedPortcastScacs.length === 0) {
      message.warning('Please select at least one carrier');
      return;
    }
    importPortcastMutation.mutate(selectedPortcastScacs);
  };

  const handleSubmit = (values: any) => {
    if (modalMode === 'create') {
      createMutation.mutate(values);
    } else if (selectedCarrier) {
      updateMutation.mutate({ id: selectedCarrier.id, values });
    }
  };

  const columns = [
    {
      title: 'SCAC',
      dataIndex: 'scac',
      key: 'scac',
      width: 100,
      sorter: (a: Carrier, b: Carrier) => a.scac.localeCompare(b.scac),
      render: (text: string) => <code style={{ fontWeight: 600 }}>{text}</code>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Carrier, b: Carrier) => a.name.localeCompare(b.name),
    },
    {
      title: 'Short Name',
      dataIndex: 'shortName',
      key: 'shortName',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 180,
      render: (record: Carrier) => (
        <Space size={4}>
          {record.sctc && <Tag color="purple">SCTC</Tag>}
          {record.portcastEnabled && <Tag color="blue">Portcast</Tag>}
        </Space>
      ),
    },
    {
      title: 'Bookings',
      dataIndex: 'bookings_count',
      key: 'bookings_count',
      width: 100,
      sorter: (a: Carrier, b: Carrier) => a.bookings_count - b.bookings_count,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: Carrier) => record.active === value,
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (record: Carrier) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEdit(record),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Deactivate',
                danger: true,
                onClick: () => handleDelete(record.id),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ color: 'var(--color-negative)' }}>Unable to Load Carriers</h2>
        <Button type="primary" onClick={() => window.location.reload()} style={{ marginTop: '24px' }}>
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 className="text-h1">Carriers</h1>
          <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage shipping carriers and SCAC codes
          </p>
        </div>
        <Space>
          <Button icon={<CloudDownloadOutlined />} onClick={handlePortcastImport}>
            Import from Portcast
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="large">
            Add Carrier
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search carriers..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            {carriers.length} carriers found
          </div>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={carriers}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      </Card>

      <Modal
        title={modalMode === 'create' ? 'Add Carrier' : 'Edit Carrier'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="scac"
            label="SCAC Code"
            rules={[
              { required: true, message: 'SCAC is required' },
              { len: 4, message: 'SCAC must be exactly 4 characters' },
            ]}
          >
            <Input
              placeholder="e.g., ONEY"
              maxLength={4}
              style={{ textTransform: 'uppercase' }}
              disabled={modalMode === 'edit'}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g., Ocean Network Express" />
          </Form.Item>

          <Form.Item name="shortName" label="Short Name">
            <Input placeholder="e.g., ONE" maxLength={24} />
          </Form.Item>

          <Form.Item name="sctc" label="SCTC Contract" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="portcastEnabled" label="Portcast Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          {modalMode === 'edit' && (
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {modalMode === 'create' ? 'Create' : 'Save'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Import Portcast Carriers"
        open={portcastModalVisible}
        onCancel={() => setPortcastModalVisible(false)}
        onOk={handlePortcastSubmit}
        okText={`Import ${selectedPortcastScacs.length} Carrier${selectedPortcastScacs.length !== 1 ? 's' : ''}`}
        okButtonProps={{ disabled: selectedPortcastScacs.length === 0, loading: importPortcastMutation.isPending }}
        width={500}
      >
        <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
          Select carriers to import. Existing carriers will have Portcast enabled.
        </p>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Checkbox.Group
            value={selectedPortcastScacs}
            onChange={(values) => setSelectedPortcastScacs(values as string[])}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {portcastCarriers.map((c) => (
                <Checkbox key={c.scac} value={c.scac} style={{ width: '100%' }}>
                  <Space>
                    <code style={{ fontWeight: 600 }}>{c.scac}</code>
                    <span>{c.name}</span>
                    {c.exists && <Tag color="green">Exists</Tag>}
                  </Space>
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </div>
      </Modal>
    </div>
  );
};

export default CarriersList;
